import React, { useState, useEffect } from "react"
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined"
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import { Spinner } from "reactstrap"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import axios from "axios"
import config from "config/config"
import VisitsChart from "newcomponents/customers-monthly"

export default function CustomerTrendsDashboard({
  viewMode,
  fromDate,
  toDate,
}) {
  const mode = useSelector(state => state.Layout.layoutMode)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const [loading, setLoading] = useState(false)
  const [timeline, setTimeline] = useState([])
  const [newCustomersTimeline, setNewCustomersTimeline] = useState([])
  const [repeatCustomersTimeline, setRepeatCustomersTimeline] = useState([])
  const [totalCustomers, setTotalCustomers] = useState(0)
  const [totalNewCustomers, setTotalNewCustomers] = useState(0)
  const [totalRepeatCustomers, setTotalRepeatCustomers] = useState(0)
  const [selectedCustomers, setSelectedCustomers] = useState(["new", "repeat"])
  const [showDropdown, setShowDropdown] = useState(false)

  const isCustomDateRange = fromDate && toDate

  const fetchCustomers = async () => {
    if (!activeOrganization?.id) return

    setLoading(true)
    try {
      const res = await axios.get(
        `${config.backendUrl}/customers/${isConsolidatedView ? organizations.map(org => org.Organization.id).join(",") : activeOrganization.id}`,
        {
          params: { fromDate, toDate },
          headers: { Authorization: `Bearer ${userData.token}` },
          skipLoader: true,
        },
      )

      const data = res.data

      setTotalCustomers(data.totalCustomers || 0)
      setTotalNewCustomers(data.newCustomers || 0)
      setTotalRepeatCustomers(data.repeatCustomers || 0)

      // Use date range returned by backend so chart always matches the data
      const start = new Date(data.dateRange?.startDate || new Date(new Date().getFullYear(), new Date().getMonth() - 11, 1))
      const end = new Date(data.dateRange?.endDate || new Date())

      // Generate timeline dynamically based on start and end date
      const timelineKeys = []
      const timelineDates = []
      let current = new Date(start.getFullYear(), start.getMonth(), 1)

      while (current <= end) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`
        timelineKeys.push(key)
        timelineDates.push(new Date(current))
        current.setMonth(current.getMonth() + 1)
      }

      // Map backend timeline data into keys
      const timelineMap = {}
      timelineKeys.forEach((key, index) => {
        timelineMap[key] = {
          new: data.newCustomersTimeline?.[index] || 0,
          repeat: data.repeatCustomersTimeline?.[index] || 0,
        }
      })

      const sortedTimeline = timelineDates.map(date =>
        date.toLocaleString("default", { month: "short", year: "numeric" }),
      )

      const newTimelineData = timelineKeys.map(key => timelineMap[key].new)
      const repeatTimelineData = timelineKeys.map(
        key => timelineMap[key].repeat,
      )

      setTimeline(sortedTimeline)
      setNewCustomersTimeline(newTimelineData)
      setRepeatCustomersTimeline(repeatTimelineData)
    } catch (err) {
      console.error("Error fetching customers:", err)
      setTimeline([])
      setNewCustomersTimeline([])
      setRepeatCustomersTimeline([])
      setTotalCustomers(0)
      setTotalNewCustomers(0)
      setTotalRepeatCustomers(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [
    activeOrganization,
    viewMode,
    fromDate,
    toDate,
    isConsolidatedView,
    organizations,
  ])

  const toggleCustomerFilter = type => {
    setSelectedCustomers(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type],
    )
  }

  const stats = [
    {
      icon: GroupOutlinedIcon,
      value: totalNewCustomers,
      label: "New Alumni",
      type: "new",
    },
    {
      icon: GroupOutlinedIcon,
      value: totalCustomers,
      label: "Total Alumni",
      type: "total",
    },
    {
      icon: HowToRegOutlinedIcon,
      value: totalRepeatCustomers,
      label: "Repeat Alumni",
      type: "repeat",
    },
  ].filter(
    stat => stat.type === "total" || selectedCustomers.includes(stat.type),
  )

  const series1 = selectedCustomers.includes("new") ? newCustomersTimeline : []
  const series2 = selectedCustomers.includes("repeat")
    ? repeatCustomersTimeline
    : []

  const isEmpty = !loading && totalCustomers === 0

  return (
    <div
      className="br"
      style={{
        width: "100%",
        background: mode === "dark" ? "#252F40" : "#fff",
        padding: 24,
        boxShadow:
          mode === "dark"
            ? "0 5px 14px rgba(0,0,0,0.5)"
            : "0 5px 14px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 32,
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "#fff" : "#111827",
          }}
        >
          Alumni Trends
        </h2>

        {!isEmpty && (
        <div
          style={{
            display: "flex",
            gap: 8,
            backgroundColor: mode === "dark" ? "#F3F4F6" : "#F3F4F6",
            borderRadius: 8,
            padding: 4,
            alignItems: "center",
            position: "relative",
          }}
        >
          {/* Customer Filter Dropdown */}
            <button
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                backgroundColor: mode === "dark" ? "#F3F4F6" : "#F3F4F6",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: mode === "dark" ? "#9CA3AF" : "#6B6B6C",
                cursor: "pointer",
              }}
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <FilterAltOutlinedIcon />
            </button>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  background: mode === "dark" ? "#1F2937" : "#fff",
                  boxShadow:
                    mode === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.5)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                  borderRadius: 8,
                  padding: 8,
                  zIndex: 20,
                  minWidth: 160,
                  border:
                    mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                }}
              >
                {["new", "repeat"].map(type => (
                  <label
                    key={type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      padding: 8,
                      fontSize: "0.875rem",
                      color: mode === "dark" ? "#D1D5DB" : "#111827",
                      borderRadius: 4,
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={e =>
                      (e.currentTarget.style.backgroundColor =
                        mode === "dark" ? "#374151" : "#F3F4F6")
                    }
                    onMouseLeave={e =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(type)}
                      onChange={() => toggleCustomerFilter(type)}
                      style={{ cursor: "pointer" }}
                    />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </label>
                ))}
              </div>
            )}
        </div>
        )}
      </div>

      {isEmpty ? (
        <div
          style={{
            padding: "48px 24px",
            textAlign: "center",
            color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            fontSize: "0.9375rem",
          }}
        >
          Add some of your alumni to analyze their trends on the{" "}
          <Link
            to="/dashboard/customers"
            style={{ color: mode === "dark" ? "#60A5FA" : "#2990DF", fontWeight: 600 }}
          >
            Alumni
          </Link>{" "}
          page.
        </div>
      ) : (
      <>
      {/* Stats Cards */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          gap: 16,
          marginBottom: 48,
        }}
      >
        {stats.map((stat, i) => {
          const Icon = stat.icon
          return (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 12,
                padding: 16,
                borderRadius: 8,
                minHeight: 80,
                flex: 1,
                boxShadow:
                  mode === "dark"
                    ? "0px 5px 14px rgba(0,0,0,0.5)"
                    : "0px 5px 14px #E5E7EB",
              }}
            >
              <Icon
                style={{
                  fontSize: 32,
                  color: mode === "dark" ? "#60A5FA" : "#2990DF",
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: "1.875rem",
                    fontWeight: "bold",
                    color: mode === "dark" ? "#fff" : "#1F2937",
                    marginBottom: 4,
                  }}
                >
                  {loading ? "…" : stat.value}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: mode === "dark" ? "#94A3B8" : "#6B7280",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Chart */}
      <div>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Spinner
              color="primary"
              style={{ height: "3rem", width: "3rem" }}
            />
          </div>
        ) : timeline.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            }}
          >
            No alumni data available for the selected period
          </div>
        ) : (
          <VisitsChart
            timeline={timeline}
            newCustomersTimeline={series1}
            repeatCustomersTimeline={series2}
          />
        )}
      </div>
      </>
      )}
    </div>
  )
}
